import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { SERVICES, SERVICE_TAGLINES } from "@/lib/content";
import { ArrowUpRight } from "lucide-react";
import VideoTile from "@/components/VideoTile";

export default function WhatWeDo() {
  return (
    <main data-testid="what-we-do-page" className="bg-white pt-32 pb-20">
      <Seo
        title="What we do"
        description="Video production, drone filming, photography, editorial content, brand development and industry reach - every service HighwaysIndustry.com offers, purpose-built for highways and infrastructure businesses."
        path="/what-we-do"
      />
      <section className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex items-center gap-3">
          <span className="h-px w-12 bg-accent-blue" />
          <span className="label-eyebrow text-accent-blue">What We Do</span>
        </div>
        <h1 className="font-heading font-black text-5xl md:text-7xl mt-6 max-w-4xl leading-[0.95] tracking-tighter">
          Real services for real businesses.
        </h1>
        <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
          Not programmes. Not jargon. The actual things we do for the highways
          and infrastructure businesses we work with.
        </p>

        {/* Our Approach preamble */}
        <Link
          to="/our-approach"
          data-testid="whatwedo-approach-link"
          className="mt-10 group inline-flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 rounded-2xl bg-slate-50 hover:bg-white border border-slate-200 hover:border-accent-blue hover:shadow-sm transition-all px-6 py-5 max-w-3xl"
        >
          <div className="flex-1">
            <div className="text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue">
              Before you pick a service
            </div>
            <p className="mt-1 font-heading font-bold text-lg md:text-xl text-slate-900 leading-tight">
              Read our approach. It explains why we build long-term
              campaigns, not one-off content.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-heading font-semibold text-accent-blue group-hover:text-slate-900 transition-colors">
            Our approach
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </Link>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-900 transition-transform duration-500 hover:-translate-y-1"
            >
              <VideoTile
                video={s.video}
                poster={s.image}
                alt={s.label}
                overlay
              />

              {/* Top badge */}
              <div className="absolute top-5 left-5 right-5 z-10 flex items-center justify-between">
                <span className="inline-flex items-center gap-2 bg-white/85 backdrop-blur px-3 py-1.5 rounded-full text-[10px] font-mono text-slate-900 tracking-widest uppercase">
                  {String(i + 1).padStart(2, "0")} · Service
                </span>
                <span className="h-9 w-9 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-slate-900 group-hover:bg-accent-blue group-hover:text-white transition-colors">
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>

              {/* Bottom content */}
              <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 z-10">
                {SERVICE_TAGLINES[s.slug] && (
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-cyan-300 mb-2">
                    {SERVICE_TAGLINES[s.slug]}
                  </p>
                )}
                <h2 className="font-heading font-black text-3xl md:text-4xl text-white tracking-tighter leading-tight">
                  {s.label}
                </h2>
                <p className="text-sm text-white/85 mt-3 leading-relaxed">
                  {s.short}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.deliverables.slice(0, 2).map((d) => (
                    <span
                      key={d}
                      className="text-[11px] font-medium bg-white/15 backdrop-blur text-white px-3 py-1.5 rounded-full border border-white/20"
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
